/**
 * Command palette (⌘K / Ctrl-K) — records *and* screens.
 *
 * Screens come from the same registry the sidebar uses, filtered by the same permissions, so the palette
 * can never be a back door to a screen the sidebar hides.
 *
 * Records are searched **in the database**, through `api.search_records`, which is SECURITY INVOKER — so
 * RLS decides what a caller can find. That matters more than it first sounds: a front-end search would
 * have to fetch rows in order to decide not to show them, so the existence of a record someone may not see
 * would leak through counts, timing or a debugger. Searching server-side means a record they cannot read
 * simply does not exist as far as they are concerned.
 *
 * Records come first in the list. Someone who types a locus code wants that locus, not the screen whose
 * description happens to mention one.
 *
 * v7 CORE-1 §8: customers come first of all — by name, code, the phone's last 4 digits, the door's QR or a bottle
 * (api.palette_search, the customer finder's rules) — with routes, riders and orders by code; then the common actions
 * the person may take; then the built screens, under their Part E names.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MOVED, NAV_SCREENS, menuLabel, workspaceOf } from '@rtcom/domain';
import { Bike, FileSearch, MapPin, Package, Route, Search, User, Users, Zap } from 'lucide-react';
import { useSession } from '../lib/session';
import { rpc } from '../lib/supabase';
import { SectionLabel, cx } from '../ui/primitives';
import { isBuilt } from '../routes/implementedScreens';

interface PaletteHit { kind: 'customer' | 'route' | 'rider' | 'order'; id: string; title: string; subtitle: string; matched?: string; path: string }
const PALETTE_ICON = { customer: Users, route: Route, rider: Bike, order: Package } as const;

/** The common actions (v7 CORE-1 §8), each shown only to those who may take it. */
const ACTIONS: { label: string; path: string; permission: string; words: string }[] = [
  { label: 'Find a customer', path: '/customers', permission: 'customer.view', words: 'customer search find' },
  { label: 'New customer', path: '/crm/onboarding?new=1', permission: 'customer.create', words: 'new customer onboard add start' },
  { label: 'Set a price', path: '/finance/prices', permission: 'pricing.view', words: 'price prices rate rupees variant' },
  { label: 'Print QR labels', path: '/admin/qr?view=print', permission: 'masterdata.qr.manage', words: 'qr label sticker print reprint' },
  { label: 'Look up a QR', path: '/admin/qr?view=lookup', permission: 'masterdata.view', words: 'qr scan lookup label code' },
  { label: 'Change a delivery', path: '/customers/dated-changes', permission: 'subscription.manage', words: 'change skip pause quantity delivery' },
  { label: 'Open tomorrow', path: '/delivery/plan', permission: 'delivery.plan.manage', words: 'tomorrow board cutoff' },
  { label: 'See live delivery', path: '/operations/live', permission: 'delivery.view', words: 'live rounds doors map' },
  { label: 'Open the order sheet', path: '/delivery/orders', permission: 'order.view', words: 'orders order sheet book' },
  { label: 'Open the packing orders', path: '/operations/packing-orders', permission: 'delivery.view', words: 'packing plant' },
  { label: 'Open the inbox', path: '/attention', permission: 'attention.view', words: 'inbox attention blocking' },
  { label: 'Decide approvals', path: '/admin/approvals', permission: 'approvals.view', words: 'approve approvals' },
  { label: 'Run a report', path: '/insights/reports', permission: 'reports.view', words: 'report download excel csv' },
  { label: 'Change a time of the day', path: '/admin/control-centre?tab=schedule', permission: 'config.view', words: 'cutoff time schedule' },
  { label: 'See the jobs', path: '/admin/control-centre?tab=jobs', permission: 'system.logs.view', words: 'jobs scheduler tick failed' },
  { label: 'Rider statements', path: '/workforce/incentives', permission: 'earnings.view', words: 'rider pay statements' },
  { label: 'Farmer payments', path: '/farm/payments', permission: 'farmer.settlement.view', words: 'farmer pay settlement' },
  { label: 'Milk tests', path: '/supply/collections?tab=operations.milk-tests', permission: 'quality.milk_tests.view', words: 'milk test quality card' },
  { label: 'Director dashboard', path: '/insights/director', permission: 'overview.view', words: 'director kpi figures' },
];

interface RecordHit {
  kind: 'location' | 'person';
  id: string;
  code: string | null;
  name: string;
  status: string | null;
  context: string | null;
  path: string;
  subtype: string;
}

const RECORD_ICON: Record<string, typeof MapPin> = { location: MapPin, person: User };

type Hit =
  | { type: 'found'; hit: PaletteHit }
  | { type: 'record'; record: RecordHit }
  | { type: 'action'; action: (typeof ACTIONS)[number] }
  | { type: 'screen'; screen: (typeof NAV_SCREENS)[number] };

/** Waits for typing to settle before asking the database. */
function useDebounced<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return settled;
}

/** The screens opened last, newest first (kept in this browser; AppShell records each visit). */
export function recentScreens(): string[] {
  try { return JSON.parse(localStorage.getItem('rtcom.recent') ?? '[]') as string[]; } catch { return []; }
}
export function rememberScreen(id: string) {
  try { localStorage.setItem('rtcom.recent', JSON.stringify([id, ...recentScreens().filter((x) => x !== id)].slice(0, 6))); } catch { /* storage blocked */ }
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { can } = useSession();

  const term = query.trim();
  const debounced = useDebounced(term, 180);

  const screens = useMemo(() => {
    const lower = term.toLowerCase();
    // Built screens only, never one that moved (its hub is listed), under the names the menu uses.
    const allowed = NAV_SCREENS.filter((screen) => !screen.hidden && !MOVED[screen.id] && isBuilt(screen.id) && can(screen.permission));
    // Nothing typed: the screens opened last first (D19 phase 8), then the rest in menu order.
    if (!lower) {
      const recent = recentScreens().map((id) => allowed.find((s) => s.id === id)).filter((s): s is (typeof allowed)[number] => !!s);
      return [...recent, ...allowed.filter((s) => !recent.includes(s))].slice(0, 10);
    }
    return allowed
      .filter(
        (screen) =>
          menuLabel(screen).toLowerCase().includes(lower) ||
          screen.label.toLowerCase().includes(lower) ||
          screen.description.toLowerCase().includes(lower) ||
          screen.path.toLowerCase().includes(lower),
      )
      .slice(0, 8);
  }, [term, can]);

  const actions = useMemo(() => {
    const lower = term.toLowerCase();
    if (!lower) return [];
    // v7 CORE-9: a scanned or pasted label opens its lookup in the QR Control Room.
    const label = /^\s*(DSG-[A-Z]{2,4}-\d{4,7}(?:-\d)?)\s*$/i.exec(term)?.[1]?.toUpperCase();
    const scanned = label && can('masterdata.view')
      ? [{ label: `Look up ${label}`, path: `/admin/qr?view=lookup&code=${encodeURIComponent(label)}`, permission: 'masterdata.view', words: '' }] : [];
    return [...scanned, ...ACTIONS.filter((a) => can(a.permission) && (a.label.toLowerCase().includes(lower) || a.words.includes(lower)))].slice(0, 5);
  }, [term, can]);

  const foundQuery = useQuery({
    queryKey: ['palette-search', debounced],
    enabled: open && debounced.length >= 2,
    staleTime: 15_000,
    queryFn: () => rpc<PaletteHit[]>('palette_search', { p_q: debounced, p_limit: 8 }),
  });

  // Two characters is the floor: one matches most of the database and helps nobody.
  const recordQuery = useQuery({
    queryKey: ['record-search', debounced],
    enabled: open && debounced.length >= 2,
    staleTime: 15_000,
    queryFn: () => rpc<RecordHit[]>('search_records', { p_query: debounced, p_limit: 12 }),
  });

  const records = recordQuery.data ?? [];
  const found = useMemo(() => {
    const seen = new Set<string>();
    return (foundQuery.data ?? []).filter((h) => { const k = `${h.kind}:${h.id}`; if (seen.has(k)) return false; seen.add(k); return true; });
  }, [foundQuery.data]);
  const results: Hit[] = useMemo(
    () => [
      ...found.map((hit) => ({ type: 'found' as const, hit })),
      ...records.map((record) => ({ type: 'record' as const, record })),
      ...actions.map((action) => ({ type: 'action' as const, action })),
      ...screens.map((screen) => ({ type: 'screen' as const, screen })),
    ],
    [found, records, actions, screens],
  );

  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlighted(0);
      // A frame's delay: the input does not exist until this render commits.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => setHighlighted(0), [query]);

  if (!open) return null;

  const go = (index: number) => {
    const hit = results[index];
    if (!hit) return;
    navigate(hit.type === 'screen' ? hit.screen.path : hit.type === 'record' ? hit.record.path : hit.type === 'found' ? hit.hit.path : hit.action.path);
    onClose();
  };

  return (
    <div
      className="overlay-scrim fixed inset-0 z-50 flex items-start justify-center bg-ink/20 pt-[12vh]"
      onMouseDown={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onMouseDown={(event) => event.stopPropagation()}
        className="overlay-surface w-full max-w-xl overflow-hidden rounded-[var(--radius-panel)] border border-line bg-surface shadow-[var(--shadow-overlay)]"
      >
        <div className="flex items-center gap-2 border-b border-line px-3">
          <Search size={15} className="text-ink-muted" aria-hidden />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setHighlighted((h) => Math.min(h + 1, results.length - 1));
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setHighlighted((h) => Math.max(h - 1, 0));
              } else if (event.key === 'Enter') {
                event.preventDefault();
                go(highlighted);
              } else if (event.key === 'Escape') {
                onClose();
              }
            }}
            placeholder="Find a customer (name, code, phone's last 4, door QR), a route, an order, or a screen…"
            aria-label="Search customers, records, actions and screens"
            className="h-11 w-full bg-transparent text-body outline-none"
          />
          <kbd className="font-mono text-badge text-ink-faint">ESC</kbd>
        </div>

        <ul className="max-h-[52vh] overflow-y-auto py-1" role="listbox">
          {results.length === 0 ? (
            <li className="px-3 py-6 text-center text-small text-ink-muted">
              {recordQuery.isFetching || foundQuery.isFetching
                ? 'Searching…'
                : term.length === 1
                  ? 'One more character to search records.'
                  : term
                    ? `Nothing matches “${term}”.`
                    : 'Search customers, records, actions and screens.'}
            </li>
          ) : (
            results.map((hit, index) => {
              const selected = index === highlighted;

              const first = (t: Hit['type']) => results.findIndex((h) => h.type === t) === index;
              if (hit.type === 'found') {
                const Icon = PALETTE_ICON[hit.hit.kind];
                return (
                  <li key={`f-${hit.hit.kind}-${hit.hit.id}`} role="option" aria-selected={selected}>
                    {first('found') ? <SectionLabel className="px-3 pt-1.5 pb-0.5">Customers and rounds</SectionLabel> : null}
                    <button type="button" onMouseEnter={() => setHighlighted(index)} onClick={() => go(index)}
                      className={cx('flex w-full items-center gap-2.5 px-3 py-1.5 text-left', selected ? 'bg-brand-soft' : 'hover:bg-surface-sunken')}>
                      <Icon size={13} className="shrink-0 text-ink-muted" aria-hidden />
                      <span className="min-w-0 flex-1 truncate text-body">{hit.hit.title}</span>
                      <span className="shrink-0 truncate text-small text-ink-faint">{hit.hit.subtitle}</span>
                      {hit.hit.matched ? <span className="label-text shrink-0">by {hit.hit.matched}</span> : null}
                    </button>
                  </li>
                );
              }
              if (hit.type === 'action') {
                return (
                  <li key={`a-${hit.action.path}`} role="option" aria-selected={selected}>
                    {first('action') ? <SectionLabel className="px-3 pt-2 pb-0.5">Do</SectionLabel> : null}
                    <button type="button" onMouseEnter={() => setHighlighted(index)} onClick={() => go(index)}
                      className={cx('flex w-full items-center gap-2.5 px-3 py-1.5 text-left', selected ? 'bg-brand-soft' : 'hover:bg-surface-sunken')}>
                      <Zap size={13} className="shrink-0 text-ink-muted" aria-hidden />
                      <span className="min-w-0 flex-1 truncate text-body">{hit.action.label}</span>
                    </button>
                  </li>
                );
              }

              if (hit.type === 'record') {
                const Icon = RECORD_ICON[hit.record.kind] ?? FileSearch;
                return (
                  <li key={`r-${hit.record.id}`} role="option" aria-selected={selected}>
                    {first('record') ? <SectionLabel className="px-3 pt-1.5 pb-0.5">Places and people</SectionLabel> : null}
                    <button
                      type="button"
                      onMouseEnter={() => setHighlighted(index)}
                      onClick={() => go(index)}
                      className={cx(
                        'flex w-full items-center gap-2.5 px-3 py-1.5 text-left',
                        selected ? 'bg-brand-soft' : 'hover:bg-surface-sunken',
                      )}
                    >
                      <Icon size={13} className="shrink-0 text-ink-muted" aria-hidden />
                      <span className="code-text w-36 shrink-0 truncate text-ink-muted">
                        {hit.record.code ?? '—'}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-body">{hit.record.name}</span>
                      {hit.record.context ? (
                        <span className="shrink-0 truncate text-small text-ink-faint">{hit.record.context}</span>
                      ) : null}
                      <span className="label-text shrink-0">{hit.record.subtype.replace(/_/g, ' ')}</span>
                    </button>
                  </li>
                );
              }

              return (
                <li key={`s-${hit.screen.id}`} role="option" aria-selected={selected}>
                  {first('screen') ? (
                    <SectionLabel className="px-3 pt-2 pb-0.5">Screens</SectionLabel>
                  ) : null}
                  <button
                    type="button"
                    onMouseEnter={() => setHighlighted(index)}
                    onClick={() => go(index)}
                    className={cx(
                      'flex w-full items-center gap-3 px-3 py-1.5 text-left',
                      selected ? 'bg-brand-soft' : 'hover:bg-surface-sunken',
                    )}
                  >
                    <span className="label-text w-24 shrink-0 truncate">
                      {workspaceOf(hit.screen.id)?.label ?? ''}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-body">{menuLabel(hit.screen)}</span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </div>
  );
}

/** Registers ⌘K / Ctrl-K globally. */
export function useCommandPalette(): { open: boolean; setOpen: (open: boolean) => void } {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return { open, setOpen };
}
